(() => {
  const ROOT_SELECTOR = '[data-paths-article]';

  function slugify(value = '') {
    return String(value)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }


  function createUniqueId(baseId, usedIds) {
    let id = baseId || 'section';
    let index = 2;

    while (usedIds.has(id) || document.getElementById(id)) {
      id = `${baseId}-${index}`;
      index += 1;
    }

    usedIds.add(id);

    return id;
  }


  function initPathsArticle(root) {
    if (!root || root.dataset.pathsArticleInitialized === 'true') return;

    root.dataset.pathsArticleInitialized = 'true';

    const content = root.querySelector('[data-paths-article-content]');
    const progressBar = root.querySelector('[data-paths-reading-progress]');

    const desktopTocWrap = root.querySelector(
      '[data-paths-article-toc-wrap]'
    );

    const desktopToc = root.querySelector(
      '[data-paths-article-toc]'
    );

    const mobileTocWrap = root.querySelector(
      '[data-paths-mobile-toc-wrap]'
    );

    const mobileToc = root.querySelector(
      '[data-paths-mobile-toc]'
    );

    const copyButton = root.querySelector('[data-paths-copy-link]');
    const copyLabel = root.querySelector('[data-paths-copy-label]');

    const headings = content
      ? Array.from(content.querySelectorAll('h2, h3'))
      : [];

    const tocLinks = [];
    const usedIds = new Set();


    /* =====================================================
       TABLE OF CONTENTS
       ===================================================== */

    function createTocLink(heading) {
      const link = document.createElement('a');

      link.href = `#${heading.id}`;
      link.textContent = heading.textContent.trim();

      link.className = 'paths-article-toc__link';

      if (heading.tagName.toLowerCase() === 'h3') {
        link.classList.add('paths-article-toc__link--h3');
      }

      link.dataset.targetId = heading.id;

      link.addEventListener('click', (event) => {
        event.preventDefault();

        const target = document.getElementById(heading.id);

        if (!target) return;

        target.scrollIntoView({
          behavior: window.matchMedia(
            '(prefers-reduced-motion: reduce)'
          ).matches
            ? 'auto'
            : 'smooth',
          block: 'start',
        });

        window.history.replaceState(
          null,
          '',
          `${window.location.pathname}${window.location.search}#${heading.id}`
        );

        if (
          mobileTocWrap &&
          mobileTocWrap.tagName.toLowerCase() === 'details'
        ) {
          mobileTocWrap.open = false;
        }
      });

      return link;
    }


    function buildTableOfContents() {
      if (headings.length < 2) {
        desktopTocWrap?.setAttribute('hidden', '');
        mobileTocWrap?.setAttribute('hidden', '');
        return;
      }

      headings.forEach((heading) => {
        const existingId = heading.id.trim();

        heading.id = existingId
          ? createUniqueId(existingId, usedIds)
          : createUniqueId(
              slugify(heading.textContent.trim()),
              usedIds
            );

        if (desktopToc) {
          const desktopLink = createTocLink(heading);

          desktopToc.appendChild(desktopLink);
          tocLinks.push(desktopLink);
        }

        if (mobileToc) {
          const mobileLink = createTocLink(heading);

          mobileToc.appendChild(mobileLink);
          tocLinks.push(mobileLink);
        }
      });

      desktopTocWrap?.removeAttribute('hidden');
      mobileTocWrap?.removeAttribute('hidden');
    }


    /* =====================================================
       ACTIVE TOC LINK
       ===================================================== */

    function setActiveHeading(activeId) {
      tocLinks.forEach((link) => {
        const isActive = link.dataset.targetId === activeId;

        link.classList.toggle('is-active', isActive);

        if (isActive) {
          link.setAttribute('aria-current', 'location');
        } else {
          link.removeAttribute('aria-current');
        }
      });
    }


    function initHeadingObserver() {
      if (!headings.length || !('IntersectionObserver' in window)) {
        return;
      }

      const visibleHeadings = new Map();

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              visibleHeadings.set(
                entry.target.id,
                entry.boundingClientRect.top
              );
            } else {
              visibleHeadings.delete(entry.target.id);
            }
          });

          if (visibleHeadings.size > 0) {
            const activeHeading = Array.from(
              visibleHeadings.entries()
            ).sort((a, b) => a[1] - b[1])[0];

            setActiveHeading(activeHeading[0]);
            return;
          }

          let latestHeading = null;

          headings.forEach((heading) => {
            const rect = heading.getBoundingClientRect();

            if (rect.top <= 170) {
              latestHeading = heading;
            }
          });

          if (latestHeading) {
            setActiveHeading(latestHeading.id);
          }
        },
        {
          rootMargin: '-130px 0px -68% 0px',
          threshold: 0,
        }
      );

      headings.forEach((heading) => observer.observe(heading));
    }


    /* =====================================================
       READING PROGRESS
       ===================================================== */

    function updateReadingProgress() {
      if (!progressBar || !content) return;

      const contentRect = content.getBoundingClientRect();
      const contentTop =
        window.scrollY + contentRect.top;

      const contentHeight = content.offsetHeight;
      const viewportHeight = window.innerHeight;

      const scrollableDistance = Math.max(
        contentHeight - viewportHeight * 0.35,
        1
      );

      const progress = Math.min(
        Math.max(
          (window.scrollY - contentTop + viewportHeight * 0.2) /
            scrollableDistance,
          0
        ),
        1
      );

      progressBar.style.width = `${progress * 100}%`;
    }


    let progressFrame = null;

    function requestProgressUpdate() {
      if (progressFrame !== null) return;

      progressFrame = window.requestAnimationFrame(() => {
        updateReadingProgress();
        progressFrame = null;
      });
    }


    /* =====================================================
       COPY LINK
       ===================================================== */

    async function copyArticleLink() {
      if (!copyButton) return;

      const url =
        copyButton.dataset.copyUrl || window.location.href;

      const originalLabel =
        copyLabel?.textContent || 'Copy link';

      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(url);
        } else {
          const temporaryInput = document.createElement('textarea');

          temporaryInput.value = url;
          temporaryInput.setAttribute('readonly', '');
          temporaryInput.style.position = 'fixed';
          temporaryInput.style.opacity = '0';

          document.body.appendChild(temporaryInput);

          temporaryInput.select();
          temporaryInput.setSelectionRange(
            0,
            temporaryInput.value.length
          );

          const copySucceeded = document.execCommand('copy');

          temporaryInput.remove();

          if (!copySucceeded) {
            throw new Error('Copy command failed');
          }
        }

        if (copyLabel) {
          copyLabel.textContent = 'Copied ✓';
        }

        window.setTimeout(() => {
          if (copyLabel) {
            copyLabel.textContent = originalLabel;
          }
        }, 2200);
      } catch (error) {
        if (copyLabel) {
          copyLabel.textContent = 'Copy failed';
        }

        window.setTimeout(() => {
          if (copyLabel) {
            copyLabel.textContent = originalLabel;
          }
        }, 2200);
      }
    }


    /* =====================================================
       INITIALIZATION
       ===================================================== */

    buildTableOfContents();
    initHeadingObserver();
    updateReadingProgress();

    window.addEventListener('scroll', requestProgressUpdate, {
      passive: true,
    });

    window.addEventListener('resize', requestProgressUpdate, {
      passive: true,
    });

    copyButton?.addEventListener('click', copyArticleLink);


    /* Scroll to hash after IDs have been generated */

    if (window.location.hash) {
      const hashId = decodeURIComponent(
        window.location.hash.slice(1)
      );

      window.requestAnimationFrame(() => {
        const hashTarget = document.getElementById(hashId);

        if (hashTarget) {
          hashTarget.scrollIntoView({
            behavior: 'auto',
            block: 'start',
          });
        }
      });
    }
  }


  function initAllPathsArticles(scope = document) {
    scope
      .querySelectorAll(ROOT_SELECTOR)
      .forEach(initPathsArticle);
  }


  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initAllPathsArticles();
    });
  } else {
    initAllPathsArticles();
  }


  document.addEventListener('shopify:section:load', (event) => {
    initAllPathsArticles(event.target);
  });
})();